import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  Eye,
  Loader2,
  MapPin,
  RefreshCw,
  Search,
  Send,
  Star,
  Truck,
  X,
  XCircle,
  Zap,
} from "lucide-react";
import { useOrders } from "../../hooks/useOrders";
import { OrderModal } from "../../components/orders/OrderModal";
import Loading from "../../../../ui/Loading";
import {
  carrierCities,
  carriersList,
  createShipment,
  resolveCities,
  saveCityMapping,
} from "../../../../../api/auth";
import { getApiErrorMessage } from "../../../../ui/Feedback";

const OPTION_LABELS = { open: "Ouvrable", fragile: "Fragile", exchange: "Échange" };
const validatePhone = (phone) => /^0\d{9}$/.test(String(phone || "").replace(/\s/g, ""));

/* ------------------------------------------------------------------ */
/* Choix de la ville dans le référentiel de l'agence                   */
/* ------------------------------------------------------------------ */
function CityPicker({ cities, value, onPick, suggestions = [] }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const base = needle ? cities.filter((c) => c.name.toLowerCase().includes(needle)) : cities;
    return base.slice(0, 80);
  }, [cities, q]);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 hover:border-indigo-300 dark:border-slate-700 dark:text-slate-300"
      >
        {value ? "Changer" : "Choisir"} <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <>
          <button type="button" className="fixed inset-0 z-20 cursor-default" onClick={() => setOpen(false)} aria-label="Fermer" />
          <div className="absolute left-0 top-8 z-30 w-72 rounded-xl border border-slate-200 bg-white p-2 shadow-lg dark:border-slate-700 dark:bg-slate-900">
            {suggestions.length > 0 && !q && (
              <div className="mb-2 border-b border-slate-100 pb-2 dark:border-slate-800">
                <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">Suggestions</div>
                {suggestions.map((c) => (
                  <button key={`s-${c.id}`} type="button" onClick={() => { onPick(c); setOpen(false); }}
                    className="w-full rounded-lg px-2 py-1.5 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-800">
                    {c.name}{c.price ? <span className="ml-1 text-xs text-slate-400">· {c.price} MAD</span> : null}
                  </button>
                ))}
              </div>
            )}
            <label className="relative block">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une ville…"
                className="w-full rounded-lg border border-slate-200 py-1.5 pl-7 pr-2 text-sm dark:border-slate-700" />
            </label>
            <div className="mt-1 max-h-60 overflow-y-auto">
              {list.map((c) => (
                <button key={c.id} type="button" onClick={() => { onPick(c); setOpen(false); setQ(""); }}
                  className="w-full rounded-lg px-2 py-1.5 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-800">
                  {c.name}{c.code ? <span className="ml-1 text-xs text-slate-400">({c.code})</span> : null}
                </button>
              ))}
              {!list.length && <p className="px-2 py-3 text-sm text-slate-400">Aucune ville.</p>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function CityCell({ order, resolution, override, cities, onPick }) {
  const city = override || (resolution?.status === "matched" ? resolution.city : null);
  const suggested = !override && resolution?.status === "suggested" ? resolution : null;

  let tone = "bg-rose-50 text-rose-700";
  let label = "Introuvable";
  if (city) { tone = "bg-emerald-50 text-emerald-700"; label = city.name; }
  else if (suggested) { tone = "bg-amber-50 text-amber-700"; label = `${suggested.city?.name} ?`; }
  else if (!resolution) { tone = "bg-slate-100 text-slate-500"; label = "…"; }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${tone}`}>
        <MapPin className="h-3 w-3" /> {label}
      </span>
      {suggested && (
        <button type="button" onClick={() => onPick(order, suggested.city)} className="text-xs font-semibold text-indigo-600">
          Valider
        </button>
      )}
      <CityPicker cities={cities} value={city} onPick={(c) => onPick(order, c)}
        suggestions={(suggested?.candidates || resolution?.candidates || []).slice(0, 5)} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
export default function DeliveryDispatch() {
  const { orders, loading, refresh } = useOrders({ filters: {}, page_size: 50 });

  const [carriers, setCarriers] = useState([]);
  const [connectionId, setConnectionId] = useState("");
  const [cities, setCities] = useState([]);
  const [resolutions, setResolutions] = useState({}); // {shipping_city: result}
  const [overrides, setOverrides] = useState({}); // {orderId: city}
  const [options, setOptions] = useState({}); // {orderId: {open, fragile, exchange}}
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState("");
  const [loadingCities, setLoadingCities] = useState(false);
  const [sending, setSending] = useState(null); // {done, total}
  const [results, setResults] = useState({}); // {orderId: {ok, text}}
  const [message, setMessage] = useState(null);
  const [viewOrder, setViewOrder] = useState(null);

  const carrier = carriers.find((c) => c.connection_id === connectionId) || null;

  // commandes confirmées pas encore expédiées
  const readyOrders = useMemo(
    () =>
      orders
        .filter((o) => o.local_status === "confirmed" && (!o.shipping_status || String(o.shipping_status).toLowerCase() === "pending"))
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at)),
    [orders]
  );

  const visibleOrders = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return readyOrders;
    return readyOrders.filter((o) =>
      [o.client_name, o.client_phone, o.shipping_city, o.produit].some((v) => String(v || "").toLowerCase().includes(q))
    );
  }, [readyOrders, search]);

  // 1) agences connectées (défaut présélectionnée)
  useEffect(() => {
    carriersList()
      .then((list) => {
        setCarriers(list || []);
        const preferred = (list || []).find((c) => c.is_default && c.capabilities?.create)
          || (list || []).find((c) => c.capabilities?.create);
        setConnectionId(preferred?.connection_id || "");
      })
      .catch((err) => setMessage({ type: "error", text: getApiErrorMessage(err, "Agences de livraison indisponibles.") }));
  }, []);

  // 2) à chaque agence : ses villes + traduction des villes des commandes
  const loadResolution = useCallback(async () => {
    if (!connectionId) return;
    setLoadingCities(true);
    setResolutions({});
    setOverrides({});
    try {
      const names = [...new Set(readyOrders.map((o) => o.shipping_city || ""))];
      const [cityData, resolved] = await Promise.all([
        carrierCities(connectionId),
        names.length ? resolveCities(connectionId, names) : Promise.resolve({ results: {} }),
      ]);
      setCities(cityData?.cities || []);
      setResolutions(resolved?.results || {});
    } catch (err) {
      setCities([]);
      setMessage({ type: "error", text: getApiErrorMessage(err, "Villes de l'agence indisponibles.") });
    } finally {
      setLoadingCities(false);
    }
  }, [connectionId, readyOrders]);

  useEffect(() => {
    loadResolution();
  }, [loadResolution]);

  const cityFor = (order) => {
    const override = overrides[order.id];
    if (override) return override;
    const res = resolutions[order.shipping_city || ""];
    return res?.status === "matched" ? res.city : null;
  };

  const optionsFor = (order) => ({
    open: !!order.is_open,
    fragile: !!order.is_fragile,
    exchange: !!order.is_exchange,
    ...(options[order.id] || {}),
  });

  // choix manuel : appliqué à toutes les commandes de la même ville + mémorisé
  const pickCity = async (order, city) => {
    const source = order.shipping_city || "";
    setOverrides((prev) => {
      const next = { ...prev };
      readyOrders.filter((o) => (o.shipping_city || "") === source).forEach((o) => { next[o.id] = city; });
      return next;
    });
    if (source) {
      try {
        await saveCityMapping(connectionId, source, city.id);
      } catch {
        /* la mémorisation est un confort : l'envoi reste possible */
      }
    }
  };

  const problemsOf = (order) => {
    const problems = [];
    if (!validatePhone(order.client_phone)) problems.push("téléphone invalide");
    if (!cityFor(order)) problems.push("ville à choisir");
    if (!order.shipping_address) problems.push("adresse manquante");
    return problems;
  };

  const selectedOrders = readyOrders.filter((o) => selectedIds.includes(o.id));
  const blocked = selectedOrders.filter((o) => problemsOf(o).length);
  const allSelected = visibleOrders.length > 0 && visibleOrders.every((o) => selectedIds.includes(o.id));

  const dispatch = async () => {
    if (!carrier || !selectedOrders.length || blocked.length) return;
    setSending({ done: 0, total: selectedOrders.length });
    setResults({});
    const sentIds = [];
    for (const [i, order] of selectedOrders.entries()) {
      try {
        const res = await createShipment({
          connection_id: connectionId,
          order_id: order.id,
          receiver: order.client_name || "",
          phone: String(order.client_phone || "").replace(/\s/g, ""),
          address: order.shipping_address || "",
          cod: Number(order.total_amount || 0),
          city: cityFor(order),
          comment: order.note || "",
          product: order.produit || "",
          options: optionsFor(order),
        });
        sentIds.push(order.id);
        setResults((prev) => ({ ...prev, [order.id]: { ok: true, text: res.tracking_number || "Envoyé" } }));
      } catch (err) {
        setResults((prev) => ({ ...prev, [order.id]: { ok: false, text: getApiErrorMessage(err, "Échec de l'envoi.") } }));
      }
      setSending({ done: i + 1, total: selectedOrders.length });
    }
    setSending(null);
    setMessage({
      type: sentIds.length === selectedOrders.length ? "success" : "error",
      text: `${sentIds.length}/${selectedOrders.length} commande(s) envoyée(s) via ${carrier.agency_name}.`,
    });
    // les commandes en échec restent sélectionnées pour corriger puis réessayer
    setSelectedIds((prev) => prev.filter((id) => !sentIds.includes(id)));
    await refresh();
  };

  return (
    <div className="page-shell space-y-5">
      {/* En-tête */}
      <div className="page-header-card flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">Livraison</div>
          <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">Expédier les commandes</h1>
          <p className="mt-1 text-sm text-slate-500">
            Choisissez l'agence pour cet envoi. Les villes sont traduites automatiquement pour l'agence choisie.
          </p>
        </div>
        <button type="button" onClick={() => { refresh(); loadResolution(); }} disabled={loading} className="btn-outline self-start">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </button>
      </div>

      {message && (
        <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-sm ${message.type === "success" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-rose-200 bg-rose-50 text-rose-700"}`}>
          {message.type === "success" ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          <span className="flex-1 font-medium">{message.text}</span>
          <button type="button" onClick={() => setMessage(null)} aria-label="Fermer"><X size={16} /></button>
        </div>
      )}

      {/* Choix de l'agence */}
      <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {carriers.map((c) => {
          const active = c.connection_id === connectionId;
          const canShip = !!c.capabilities?.create;
          return (
            <button key={c.connection_id} type="button" disabled={!canShip || !!sending}
              onClick={() => setConnectionId(c.connection_id)}
              title={canShip ? "" : "Cette agence ne permet pas de créer des colis via l'API"}
              className={`surface-card flex items-start gap-3 rounded-xl border p-4 text-left transition ${active ? "border-indigo-500 bg-indigo-50" : "border-slate-200 bg-white hover:border-slate-300 dark:border-slate-800 dark:bg-slate-900"} ${canShip ? "" : "cursor-not-allowed opacity-50"}`}>
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                {c.logo_url ? <img src={c.logo_url} alt="" className="h-8 w-8 object-contain" /> : <Truck className="h-5 w-5" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {c.agency_name}
                  {c.is_default && <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" aria-label="Par défaut" />}
                  {c.capabilities?.webhook && <Zap className="h-3.5 w-3.5 text-emerald-500" aria-label="Suivi temps réel" />}
                </span>
                <span className="block truncate text-xs text-slate-500">{c.connection_name}</span>
                <span className="mt-1 flex flex-wrap gap-1">
                  {(c.shipment_options || []).map((o) => (
                    <span key={o} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">{OPTION_LABELS[o]}</span>
                  ))}
                  {!canShip && <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-600">Suivi uniquement</span>}
                </span>
              </span>
            </button>
          );
        })}
        {!carriers.length && (
          <div className="empty-state col-span-full rounded-xl bg-slate-50 p-6 text-center text-sm text-slate-500">
            Aucune agence de livraison connectée. Connectez-en une dans Livraison &gt; Agences.
          </div>
        )}
      </section>

      {/* Commandes */}
      <section className="surface-card overflow-hidden rounded-xl border border-slate-200 bg-white p-0 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 dark:border-slate-800">
          <div className="text-sm text-slate-500">
            <span className="font-semibold text-slate-900 dark:text-slate-100">{readyOrders.length}</span> commande(s) confirmée(s) à expédier
            {loadingCities && <span className="ml-2 inline-flex items-center gap-1"><Loader2 className="h-3.5 w-3.5 animate-spin" /> villes…</span>}
          </div>
          <label className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Client, ville, produit…"
              className="w-64 rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm dark:border-slate-700" />
          </label>
        </div>

        {loading ? (
          <div className="py-12"><Loading text="Chargement des commandes…" variant="dots" /></div>
        ) : !visibleOrders.length ? (
          <div className="empty-state m-4 rounded-lg bg-slate-50 p-10 text-center text-sm text-slate-500">Aucune commande confirmée à expédier.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">
                  <th className="w-10 px-3 py-3">
                    <input type="checkbox" className="h-4 w-4 accent-indigo-600" checked={allSelected}
                      onChange={(e) => setSelectedIds(e.target.checked ? visibleOrders.map((o) => o.id) : [])} aria-label="Tout sélectionner" />
                  </th>
                  <th className="px-3 py-3">Client</th>
                  <th className="px-3 py-3">Commande</th>
                  <th className="px-3 py-3">Ville (commande)</th>
                  <th className="px-3 py-3">Ville chez {carrier?.agency_name || "l'agence"}</th>
                  <th className="px-3 py-3">Options</th>
                  <th className="px-3 py-3 text-right">État</th>
                </tr>
              </thead>
              <tbody>
                {visibleOrders.map((order) => {
                  const problems = problemsOf(order);
                  const result = results[order.id];
                  const opts = optionsFor(order);
                  return (
                    <tr key={order.id} className="border-t border-slate-100 align-middle hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800">
                      <td className="px-3 py-3">
                        <input type="checkbox" className="h-4 w-4 accent-indigo-600" checked={selectedIds.includes(order.id)}
                          onChange={(e) => setSelectedIds((prev) => e.target.checked ? [...new Set([...prev, order.id])] : prev.filter((id) => id !== order.id))} />
                      </td>
                      <td className="px-3 py-3">
                        <div className="font-semibold text-slate-900 dark:text-slate-100">{order.client_name || "—"}</div>
                        <div className={`text-xs ${validatePhone(order.client_phone) ? "text-slate-500" : "text-rose-600"}`}>{order.client_phone || "—"}</div>
                      </td>
                      <td className="max-w-[220px] px-3 py-3">
                        <div className="truncate text-slate-800 dark:text-slate-200" title={order.produit}>{order.produit || "—"}</div>
                        <div className="text-xs font-semibold text-slate-900 dark:text-slate-100">{order.total_amount} {order.currency}</div>
                      </td>
                      <td className="px-3 py-3 text-slate-600 dark:text-slate-300">{order.shipping_city || <span className="text-rose-600">—</span>}</td>
                      <td className="px-3 py-3">
                        <CityCell order={order} resolution={resolutions[order.shipping_city || ""]} override={overrides[order.id]}
                          cities={cities} onPick={pickCity} />
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(carrier?.shipment_options || []).map((key) => (
                            <button key={key} type="button"
                              onClick={() => setOptions((prev) => ({ ...prev, [order.id]: { ...opts, [key]: !opts[key] } }))}
                              className={`rounded-full px-2 py-0.5 text-[11px] font-semibold transition ${opts[key] ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-500 dark:bg-slate-800"}`}>
                              {OPTION_LABELS[key]}
                            </button>
                          ))}
                          {!carrier?.shipment_options?.length && <span className="text-xs text-slate-400">—</span>}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {result ? (
                            <span className={`inline-flex max-w-[240px] items-center gap-1 text-xs font-semibold ${result.ok ? "text-emerald-600" : "text-rose-600"}`} title={result.text}>
                              {result.ok ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <XCircle className="h-4 w-4 shrink-0" />}
                              <span className="truncate">{result.text}</span>
                            </span>
                          ) : problems.length ? (
                            <span className="text-xs text-amber-600">{problems.join(", ")}</span>
                          ) : (
                            <span className="text-xs text-emerald-600">Prête</span>
                          )}
                          <button type="button" onClick={() => setViewOrder(order)} className="icon-button !h-8 !w-8 rounded-lg" title="Détails">
                            <Eye className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Barre d'envoi */}
      {selectedOrders.length > 0 && (
        <div className="sticky bottom-4 z-20 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-lg dark:border-slate-700 dark:bg-slate-900">
          <div className="text-sm">
            <span className="font-semibold">{selectedOrders.length}</span> sélectionnée(s) via <span className="font-semibold">{carrier?.agency_name || "—"}</span>
            {blocked.length > 0 && <span className="ml-2 text-amber-600">· {blocked.length} à corriger (ville, téléphone ou adresse)</span>}
          </div>
          <button type="button" onClick={dispatch} disabled={!carrier || !!sending || blocked.length > 0} className="btn-primary disabled:opacity-50">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? `Envoi ${sending.done}/${sending.total}…` : `Expédier via ${carrier?.agency_name || "…"}`}
          </button>
        </div>
      )}

      {viewOrder && <OrderModal order={viewOrder} onClose={() => setViewOrder(null)} onSaved={refresh} city={cities} />}
    </div>
  );
}
